import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Alert,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Sell as TagsIcon,
  MonitorHeart as HealthIcon,
  History as HistoryIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "oidc-react";
import { useUser } from "@/hooks/useUser";
import {
  usePermissions,
  useIsAdmin,
  PERMISSIONS,
} from "@/hooks/usePermissions";
import { ROUTES, APP_NAME } from "@/constants";
import { getUserEmail, getUserDisplayName, getUserRole } from "@/types/auth";

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface AdminMenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  permission?: string;
  roles?: string[];
}

const ADMIN_MENU_ITEMS: AdminMenuItem[] = [
  {
    path: ROUTES.ADMIN_DASHBOARD,
    label: "Dashboard",
    icon: <DashboardIcon />,
    permission: PERMISSIONS.VIEW_SYSTEM_STATS,
  },
  {
    path: ROUTES.ADMIN_USERS,
    label: "Användarhantering",
    icon: <PeopleIcon />,
    permission: PERMISSIONS.MANAGE_USERS,
  },
  {
    path: ROUTES.ADMIN_CONTROL_TYPES,
    label: "Kontrolltyper",
    icon: <CategoryIcon />,
    permission: PERMISSIONS.VIEW_CONTROL_TYPES,
  },
  {
    path: ROUTES.ADMIN_ISSUERS,
    label: "Issuers",
    icon: <BusinessIcon />,
    permission: PERMISSIONS.VIEW_CONTROL_TYPES,
  },
  {
    path: ROUTES.ADMIN_TAGS,
    label: "Taggvokabulär",
    icon: <TagsIcon />,
    permission: PERMISSIONS.MANAGE_SYSTEM_CONFIG,
  },
  {
    path: ROUTES.ADMIN_SYSTEM_HEALTH,
    label: "Systemhälsa",
    icon: <HealthIcon />,
    permission: PERMISSIONS.VIEW_PERFORMANCE_METRICS,
  },
  {
    path: ROUTES.ADMIN_AUDIT_LOGS,
    label: "Revisionsloggar",
    icon: <HistoryIcon />,
    permission: PERMISSIONS.VIEW_AUDIT_LOGS,
  },
];

const DRAWER_WIDTH = 280;

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOutRedirect } = useAuth();
  const { user } = useUser();
  const { hasPermission } = usePermissions();
  const isAdmin = useIsAdmin();

  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const userEmail = getUserEmail(user);
  const userDisplayName = getUserDisplayName(user);
  const userRole = getUserRole(user);

  // Redirect non-admin users
  if (!isAdmin) {
    return (
      <Box sx={{
        p: 3
      }}>
        <Alert severity="error">
          Du har inte behörighet att komma åt administratörspanelen.
        </Alert>
      </Box>
    );
  }

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    signOutRedirect();
    handleMenuClose();
  };

  const handleBackToApp = () => {
    navigate(ROUTES.DASHBOARD);
  };

  // Close mobile drawer after navigation (better UX than onClick)
  useEffect(() => {
    // Only close on mobile (xs/sm breakpoints)
    if (drawerOpen && window.innerWidth < 900) {
      setDrawerOpen(false);
    }
  }, [location.pathname]);

  // Filter menu items based on permissions
  const visibleMenuItems = ADMIN_MENU_ITEMS.filter((item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          background: "linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)",
          color: "white",
        }}
      >
        <AdminIcon sx={{ mr: 1 }} />
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>

      <List sx={{ px: 1, pt: 2 }}>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                "&.Mui-selected": {
                  backgroundColor: "rgba(46, 125, 50, 0.12)",
                  "&:hover": {
                    backgroundColor: "rgba(46, 125, 50, 0.2)",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(46, 125, 50, 0.08)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color:
                    location.pathname === item.path
                      ? "primary.main"
                      : "inherit",
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      color:
                        location.pathname === item.path
                          ? "primary.main"
                          : "inherit",
                    },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mt: 2, mx: 2 }} />

      <List sx={{ px: 1, pt: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleBackToApp}
            sx={{
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText
              primary="Tillbaka till appen"
              slotProps={{
                primary: { sx: { fontSize: "0.875rem" } },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%" },
          ml: { md: drawerOpen ? `${DRAWER_WIDTH}px` : 0 },
          transition: "width 0.3s ease, margin 0.3s ease",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: "none", md: "inline-flex" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {APP_NAME} - Administration
          </Typography>

          {/* User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              {userDisplayName || userEmail || "Admin"}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2" sx={{
            color: "text.secondary"
          }}>
            {userDisplayName || userEmail || "Admin"}
          </Typography>
        </MenuItem>
        <MenuItem disabled>
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            {userRole || "Okänd roll"}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Logga ut
        </MenuItem>
      </Menu>

      {/* Navigation Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            transform: drawerOpen
              ? "translateX(0)"
              : `translateX(-${DRAWER_WIDTH}px)`,
            transition: "transform 0.3s ease",
            borderRight: "1px solid rgba(0, 0, 0, 0.12)",
          },
        }}
        open={drawerOpen}
      >
        {drawer}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
          disableRestoreFocus: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%" },
          transition: "width 0.3s ease",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: "#f5f5f5",
            overflow: "auto",
            height: "calc(100vh - 64px)", // Subtract toolbar height
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
